import legacyRu from '../ru.json'

function expand(flat: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.')
    let cur = out
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i]!
      if (typeof cur[p] !== 'object' || cur[p] === null) {
        cur[p] = {}
      }
      cur = cur[p] as Record<string, unknown>
    }
    cur[parts[parts.length - 1]!] = value
  }
  return out
}

export const ru = {
  translation: {
    ...expand(legacyRu as Record<string, string>),
    common: {
      ...((expand(legacyRu as Record<string, string>).common as Record<string, unknown>) ?? {}),
      loading: 'Загрузка...',
      error: 'Ошибка',
      cancel: 'Отмена',
      save: 'Сохранить',
      delete: 'Удалить',
      retry: 'Повторить',
      actions: {
        save: 'Сохранить',
        cancel: 'Отмена',
        delete: 'Удалить',
      },
      'error.generic': 'Произошла ошибка. Попробуйте еще раз.',
      unsaved: {
        title: 'Несохранённые изменения',
        body: 'У вас есть несохранённые изменения. Переключив workspace, вы их потеряете.',
        confirm: 'Переключить',
        cancel: 'Отмена',
      },
    },
    auth: {
      ...((expand(legacyRu as Record<string, string>).auth as Record<string, unknown>) ?? {}),
      login: {
        title: 'Войти',
        subtitle: 'Добро пожаловать в Creo Studio',
        username: 'Логин',
        username_placeholder: 'Введите логин',
        password: 'Пароль',
        password_placeholder: 'Введите пароль',
        password_show: 'Показать пароль',
        password_hide: 'Скрыть пароль',
        submit: 'Войти',
        forgot: 'Забыли пароль?',
        'no-account': 'Нет аккаунта?',
        'register-link': 'Зарегистрироваться',
        error: {
          invalid: 'Неверный логин или пароль',
          network: 'Ошибка сети. Попробуйте еще раз.',
        },
      },
      forgot: {
        title: 'Забыли пароль?',
        body: 'Введите ваш username — мы свяжемся с админом через Telegram для генерации временного пароля.',
        username: 'Логин',
        submit: 'Отправить запрос',
        success: 'Запрос отправлен. Ожидайте сообщения в Telegram.',
        error_required: 'Введите логин',
        'back-to-login': 'Вспомнили пароль? Войти',
      },
      logout: {
        success: 'Вы вышли',
      },
      invite: {
        accepting: 'Принятие приглашения...',
        success: 'Вы присоединились к {{name}}',
        error_expired: 'Приглашение просрочено. Запросите новое.',
        error_invalid: 'Приглашение недействительно или уже использовано.',
        error_already_member: 'Вы уже являетесь участником этого рабочего пространства.',
        go_dashboard: 'На главную',
        go_login: 'Войти',
      },
      register: {
        title: 'Создать аккаунт',
        username: 'Логин',
        username_checking: 'Проверка...',
        username_available: 'Логин доступен',
        password: 'Пароль',
        confirm_password: 'Повторите пароль',
        passwords_mismatch: 'Пароли не совпадают',
        email: 'Email',
        email_required: 'Укажите корректный email',
        tos_accept: 'Я соглашаюсь с условиями использования',
        submit: 'Создать',
        have_account: 'Уже есть аккаунт?',
        login_link: 'Войти',
        success: 'Регистрация успешна. Админ должен одобрить аккаунт.',
        error_username_taken: 'Этот логин уже занят',
        error_email_taken: 'Email уже используется',
        password_strength_prefix: 'Надёжность:',
        password_too_weak: 'слишком слабый',
        password_strength: {
          weak: 'Слабый',
          medium: 'Средний',
          strong: 'Сильный',
          very_strong: 'Очень сильный',
        },
      },
    },
    settings: {
      title: 'Настройки',
      tabs: {
        account: 'Аккаунт',
        apikeys: 'API ключи',
      },
      apikeys: {
        title: 'API ключи',
        description:
          'Ключи для внешних сервисов. Хранятся зашифрованными в workspace. Только админ может добавлять и удалять.',
        empty: 'Ключей ещё нет. Добавьте первый.',
        not_admin: 'Только админ workspace может управлять ключами.',
        no_workspace:
          'Выберите workspace в заголовке — ключи привязаны к рабочему пространству.',
        column: {
          provider: 'Сервис',
          value: 'Ключ',
          created: 'Добавлен',
          actions: 'Действия',
        },
        provider_label: 'Сервис',
        key_label: 'Ключ',
        provider: {
          elevenlabs: 'ElevenLabs',
          fal: 'fal.ai',
          deepl: 'DeepL',
        },
        add_button: 'Добавить ключ',
        cancel: 'Отмена',
        delete: 'Удалить',
        delete_confirm: 'Удалить ключ {{provider}}?',
        add_success: 'Ключ добавлен',
        add_error: 'Не удалось добавить ключ',
        delete_success: 'Ключ удалён',
        delete_error: 'Не удалось удалить',
        load_error: 'Не удалось загрузить ключи',
        key_required: 'Введите ключ',
      },
      password: {
        heading: 'Смена пароля',
        current: 'Текущий пароль',
        new: 'Новый пароль',
        confirm: 'Повторите новый пароль',
        submit: 'Сменить пароль',
        success: 'Пароль изменён',
        error_current_wrong: 'Текущий пароль неверен',
        error_mismatch: 'Пароли не совпадают',
        error_too_weak: 'Пароль слишком слабый',
        coming_soon:
          'Coming soon — endpoint pending Go-fix (parity-gap row 2).',
      },
    },
    errors: {
      something_wrong: 'Что-то пошло не так',
      reload: 'Перезагрузить страницу',
      support_id: 'ID для поддержки: {{id}}',
      not_found: 'Страница не найдена.',
      forbidden: 'Доступ запрещён.',
      server_error: 'Ошибка сервера. Попробуйте позже.',
      go_home: 'На главную',
    },
    autoUpdate: {
      title: 'Доступно обновление',
      countdown: 'Перезагрузка через {{n}}',
    },
    nav: {
      dashboard: 'Дашборд',
      projects: 'Проекты',
      data: 'Данные',
      quickTasks: 'Быстрые задачи',
      voices: 'Голоса',
      overlay: 'Блики',
      compress: 'Сжатие видео',
      audioMask: 'Маскировка аудио',
      transcription: 'Транскрипция',
      translate: 'Перевод',
      templates: 'Шаблоны',
      stats: 'Статистика',
      settings: 'Настройки',
      notifications: 'Уведомления',
      admin: 'Админка',
      workspace: 'Рабочее пространство',
      updates: 'Обновления',
      support: 'Баги и идеи',
      faq: 'FAQ',
    },
    sidebar: {
      label: 'Основная навигация',
      section_main: 'Основное',
      section_data: 'Данные',
      section_system: 'Система',
    },
    header: {
      menu: 'Меню',
      stats: 'Мини-статистика',
      personal: 'Личное пространство',
      theme: 'Тема',
      language: 'Язык',
      logout: 'Выйти',
      settings_soon: 'Настройки — скоро',
      switcher_soon: 'Переключатель workspace — в E2',
      bell_soon: 'Уведомления — в E3',
      // Mini-stats labels — verbatim from legacy `lang-data.js` (`hdr.*`).
      running: 'В РАБОТЕ',
      queue: 'ОЧЕРЕДЬ',
      errors: 'ОШИБКИ',
      done: 'Готово',
      running_tip: 'В работе — открыть задачи',
      queue_tip: 'В очереди — открыть задачи',
      errors_tip: 'Ошибок сегодня — открыть задачи',
      done_tip: 'Всего выполнено — открыть задачи',
      ws_personal: 'Личный',
      ws_title: 'Рабочая среда',
      ws_manage: 'Управление средами',
      search: 'Поиск',
    },
    test: {
      greeting: 'Привет',
      welcome: 'Привет, {{name}}',
    },
    notif: {
      task_done: 'Задача {{taskId}} завершена',
    },
    notifications: {
      title: 'Уведомления',
      bell_aria: 'Уведомления',
      empty: 'Уведомлений нет.',
      empty_unread: 'Всё прочитано.',
      error_load: 'Не удалось загрузить уведомления.',
      panel_title: 'Уведомления',
      see_all: 'Все уведомления',
      mark_all_read: 'Отметить все как прочитанные',
      mark_read: 'Отметить прочитанным',
      mark_read_success: 'Отмечено',
      mark_all_read_success: 'Отмечено: {{count}}',
      tab: {
        all: 'Все',
        unread: 'Непрочитанные',
      },
      delete: 'Удалить',
      delete_success: 'Уведомление удалено',
      delete_error: 'Не удалось удалить',
      pagination: {
        prev: 'Предыдущая',
        next: 'Следующая',
        page: 'Страница {{page}} из {{pages}}',
      },
      announcement: {
        aria: 'Системное объявление',
      },
      type: {
        task_done: 'Задача завершена',
        task_error: 'Ошибка задачи',
        member_added: 'Участник добавлен',
        colleague_added: 'Коллега добавлен',
        team_update: 'Обновление команды',
        account_approved: 'Аккаунт подтверждён',
        key_shared: 'Ключ расшарен',
        warning: 'Предупреждение',
        error_alert: 'Ошибка',
        resource_high: 'Высокая нагрузка',
        resource_normal: 'Нагрузка в норме',
      },
    },
    balance: {
      el: 'EL',
      fal: 'FAL',
      el_label: 'EL баланс',
      fal_label: 'FAL баланс',
      pending: 'не настроено',
      error: 'недоступен',
      remaining: 'Остаток: {{value}}',
      remaining_currency: 'Баланс: {{value}}',
    },
    admin_monitor: {
      title: 'Системный монитор',
      running_tasks: 'Выполняются',
      pending_tasks: 'В очереди',
      disk: 'Диск',
      memory: 'Память',
      error_load: 'Не удалось загрузить монитор',
    },
    stats: {
      title: 'Статистика',
      export_button: 'Экспорт XLSX',
      period: {
        today: 'Сегодня',
        '7d': '7 дней',
        '30d': '30 дней',
        all: 'Всё время',
      },
      kpi: {
        total_rows: 'Всего строк',
        rows_done: 'Готово',
        rows_failed: 'Ошибки',
        el_chars: 'EL символов',
        fal_minutes: 'FAL минут',
        voice_clones: 'Клонов голоса',
        storage: 'Хранилище',
      },
      charts: {
        daily_title: 'Динамика по дням',
        engine_title: 'Распределение по движку',
        loading: 'Загрузка графика…',
        empty: 'Данных за период нет.',
      },
      error_load: 'Не удалось загрузить статистику.',
      mini_chart: {
        title: 'Активность очереди (последние {{min}} мин)',
        running: 'Выполняются',
        queued: 'В очереди',
      },
    },
    dashboard: {
      hero: {
        greeting_morning: 'Доброе утро',
        greeting_day: 'Добрый день',
        greeting_evening: 'Добрый вечер',
        greeting_night: 'Доброй ночи',
        in_workspace: 'В workspace «{{name}}»',
        personal_context: 'Личное пространство',
      },
      // Ported 1:1 from legacy `dash.*` namespace in lang-data.js.
      subtitle: 'Вот что происходит на платформе',
      updated_at: 'обновлено {{time}}',
      stats_btn: 'Статистика',
      projects_btn: 'Мои проекты',
      onboarding: {
        welcome: 'Добро пожаловать в AI Creo Studio!',
        welcome_hint: 'Сделай эти шаги чтобы начать создавать AI видео:',
        dismiss: 'Скрыть',
        step1: 'Добавь ElevenLabs API ключ',
        step2: 'Добавь FAL.ai API ключ',
        step3: 'Клонируй голос',
        step4: 'Создай первый проект',
        step5: 'Запусти обработку!',
        empty_title: 'Начни создавать AI видео!',
        empty_step1: 'Добавь API ключи в настройках',
        empty_step2: 'Создай проект или быструю задачу',
        create_project_btn: '+ Создать проект',
      },
      qa: {
        quick: 'Быстрая задача',
        project: 'Создать проект',
        keys: 'API ключи',
        tasks: 'Все задачи',
      },
      stats: {
        projects: 'Проектов',
        rows: 'Строк всего',
        audio: 'Аудио готово',
        video: 'Видео готово',
        disk: 'Диск',
      },
      overlay: {
        title: 'Блики на видео',
        done: 'Готово',
        queue: 'В очереди',
      },
      today: {
        title: 'Сделано сегодня',
        audio: 'Аудио',
        video: 'Видео',
        errors: 'Ошибки',
        chars: 'Символов',
      },
      recent: {
        title: 'Последние результаты',
        all_tasks: 'Все задачи →',
        empty: 'Нет готовых файлов',
        download_audio: 'Скачать аудио',
        download_video: 'Скачать видео',
      },
      recent_projects: {
        title: 'Последние проекты',
        title_h: 'Последние проекты',
        all: 'Все проекты →',
        no_desc: 'Без описания',
        rows_short: 'строк',
        empty: 'Проектов ещё нет.',
        error_load: 'Не удалось загрузить проекты',
        view_all: 'Все проекты',
      },
      status: {
        active: 'активный',
        done: 'готово',
        archived: 'архив',
      },
      queue: {
        title: 'Очередь обработки',
        idle: 'Нет задач',
        empty: 'Активных задач нет',
        running: 'Выполняются',
        queued: 'В очереди',
        active_n: '{{n}} активных',
        refresh: 'Обновить',
      },
      activity: {
        title: 'Последняя активность',
        empty: 'Активности ещё нет.',
        error_load: 'Не удалось загрузить активность',
        no_text: '(без текста)',
      },
    },
    projects: {
      title: 'Проекты',
      create_button: '+ Новый проект',
      subtitle: 'Все твои проекты в одном месте',
      empty: 'Проектов ещё нет. Создайте первый!',
      empty_cta: 'Создать первый',
      no_results: 'Нет проектов по этому запросу.',
      error_load: 'Не удалось загрузить проекты.',
      search_placeholder: 'Поиск по названию или описанию...',
      // List-page summary, toolbar, list states — ported 1:1 from legacy
      // `pages/projects.html` + `static/js/lang-data.js` (`proj.*` keys).
      list: {
        count_projects: 'проектов',
        count_rows: 'строк',
        count_audio: 'аудио',
        count_video: 'видео',
        loading: 'Загрузка проектов...',
        show_more: 'Показать ещё',
        left: 'осталось',
        all_status: 'Все статусы',
        status_draft: 'Черновик',
        status_active: 'Активный',
        status_ready: 'Готово к обработке',
        status_archive: 'Архив',
        status_empty: 'Пустой',
        all_dates: 'Все даты',
        today: 'Сегодня',
        d3: '3 дня',
        d7: '7 дней',
        month: 'Месяц',
        sort_updated_desc: 'Обновлён ↓',
        sort_updated_asc: 'Обновлён ↑',
        sort_title_asc: 'Название А-Я',
        sort_title_desc: 'Название Я-А',
        sort_rows_desc: 'Строк ↓',
        sort_rows_asc: 'Строк ↑',
        sort_progress: 'Прогресс ↓',
        view_tiles: 'Плитки',
        view_list: 'Список',
        no_desc: 'Нет описания',
        processing: 'Обрабатывается',
        empty_title: 'Создай первый проект!',
        empty_desc:
          'Проект — это рабочее пространство для озвучки и lip-sync видео. Добавь строки с текстом и запусти обработку.',
      },
      column: {
        title: 'Название',
        status: 'Статус',
        updated: 'Обновлён',
        actions: 'Действия',
      },
      status: {
        active: 'Активный',
        archived: 'Архивный',
        unknown: '—',
      },
      detail_pending:
        'Запуск и live-мониторинг строк — в следующем релизе (E06 Phase B).',
      action: {
        open: 'Открыть',
        open_menu: 'Действия над проектом',
        delete: 'Удалить',
        pin: 'Закрепить',
        unpin: 'Открепить',
        duplicate: 'Дублировать',
        archive: 'Архивировать',
      },
      rows: {
        title: 'Строки',
        empty: 'В этом проекте ещё нет строк.',
        empty_cta: 'Добавить первую',
        error_load: 'Не удалось загрузить строки',
        add_button: 'Добавить строку',
        column: {
          number: '#',
          name: 'Название',
          text: 'Текст',
          voice: 'Голос',
          el_status: 'EL',
          fal_status: 'FAL',
          actions: 'Действия',
        },
        cell_placeholder: {
          name: 'Название…',
          text: 'Текст…',
          voice: 'Voice ID',
        },
        action: {
          delete: 'Удалить строку',
          launch: 'Запустить',
          play_output: 'Проиграть результат',
          download_output: 'Скачать',
          launch_all: 'Запустить все',
        },
        launch_success: 'Запуск инициирован',
        launch_error: 'Не удалось запустить',
        download_error: 'Не удалось скачать',
        no_rows_to_launch: 'Нет строк для запуска',
        delete_confirm: {
          title: 'Удалить строку #{{n}}?',
          body: 'Все задачи этой строки будут отменены.',
          confirm: 'Удалить',
          cancel: 'Отмена',
        },
        delete_success: 'Строка удалена',
        delete_error: 'Не удалось удалить строку',
        create_success: 'Строка добавлена',
        create_error: 'Не удалось добавить строку',
        update_error: 'Не удалось сохранить изменения',
      },
      create_dialog: {
        title: 'Новый проект',
        template_label: 'Шаблон',
        tpl_blank: 'Пустой',
        tpl_vsl: 'VSL озвучка',
        tpl_dips: 'Быстрые дипы',
        tpl_translate: 'Перевод видео',
        name_label: 'Название проекта *',
        name_placeholder: 'Например: Озвучка рекламного ролика',
        description_label: 'Описание',
        description_optional: '(необязательно)',
        description_placeholder: 'Что за проект, для кого, сроки...',
        submit: 'Создать проект',
        submitting: 'Создаю...',
        cancel: 'Отмена',
        success: 'Проект создан',
        error_name_required: 'Введи название проекта',
        error_generic: 'Не удалось создать проект',
      },
      delete_confirm: {
        title: 'Удалить проект «{{name}}»?',
        body: 'Все строки, файлы и задачи этого проекта станут недоступны. Это действие нельзя отменить.',
        confirm: 'Удалить',
        cancel: 'Отмена',
      },
      delete_success: 'Проект удалён',
      delete_error: 'Не удалось удалить проект',
      import: {
        title: 'Импортировать строки',
        subtitle:
          'Загрузите Excel, вставьте таблицу из буфера или укажите URL Google Sheets / CSV.',
        tab: {
          xlsx: 'Файл',
          paste: 'Вставить',
          url: 'URL / Sheets',
        },
        xlsx_label: 'XLSX / CSV (≤ 500 строк)',
        paste_label: 'Вставьте таблицу (TSV/CSV)',
        paste_columns_hint:
          'Колонки: Название · Текст · Аудио URL · Видео URL · Voice ID. Разделитель — Tab.',
        paste_preview_empty:
          'Пока пусто. Вставьте данные из таблицы — увидите предварительный просмотр.',
        paste_preview_count: 'Будет добавлено: {{n}} строк(и)',
        paste_preview_truncated: '(показаны первые {{shown}})',
        preview_col_name: 'Название',
        preview_col_text: 'Текст',
        preview_col_audio: 'Audio',
        preview_col_video: 'Video',
        columns_hint: 'Поддерживаемые колонки (строка-заголовок): ',
        columns_hint_files:
          'voice_file / video_file — имена файлов из вашей библиотеки голосов и видео (не URL).',
        delimiter: 'Разделитель',
        delimiter_auto: 'Авто',
        delimiter_tab: 'Tab',
        delimiter_comma: 'Запятая',
        url_label: 'Google Sheets или CSV/XLSX URL',
        url_hint:
          'Sheets — добавьте сервисный аккаунт в настройках. Иначе поддерживаются прямые CSV/XLSX ссылки.',
        errors_title: 'Ошибки строк',
        cancel: 'Отмена',
        submit: 'Импортировать',
        success: 'Импортировано: {{n}} (пропущено: {{s}})',
        partial: 'Импортировано {{n}}, есть {{e}} ошибок — посмотрите список',
        error: 'Не удалось импортировать',
        error_no_file: 'Выберите файл',
        error_no_paste: 'Вставьте данные',
        error_no_url: 'Укажите URL',
      },
      /* p2.* — verbatim labels ported from legacy `lang-data.js`
         (project-v2.html). Keys preserved so cross-referencing the
         legacy file remains painless. */
      p2: {
        back_to_projects: 'Проекты',
        loading_project: 'Загрузка...',
        badge_empty: 'Пустой',
        subtitle_prep: 'Подготовка контента и запуск обработки',
        sum_rows: 'Строк:',
        sum_ready: 'Готовы:',
        sum_running: 'В работе:',
        sum_errors: 'Ошибки:',
        sum_done: 'Готово:',
        sum_empty: 'Незаполненные:',
        label_el_model: 'Модель Eleven',
        label_video_engine: 'Видео-движок',
        btn_check: 'Проверить',
        btn_audio: 'Аудио',
        btn_all: 'Всё',
        btn_stop: 'Стоп',
        hint_selection:
          'Если строки выделены, действия применяются к ним. Если нет — ко всем готовым строкам.',
        col_name: 'Название',
        col_text: 'Текст',
        col_audio: 'Аудио',
        col_video: 'Видео',
        col_el_model: 'EL Модель',
        col_fal_engine: 'FAL Движок',
        col_num: '№',
        tasks_title: 'Задачи и результаты',
        sse_label: 'обновление',
        tasks_subtitle:
          'Здесь отображается выполнение строк проекта: статусы, ошибки, результаты и действия.',
        sel_count: 'Выбрано: {{n}}',
        btn_bulk_voice: 'Голос для выбранных',
        btn_bulk_video: 'Видео для выбранных',
        btn_delete_selected: 'Удалить выбранные',
        btn_add_row: 'Строка',
        btn_import: 'Импорт',
        btn_export: 'Экспорт',
        btn_columns: 'Колонки',
        menu_rename: 'Переименовать',
        menu_duplicate: 'Дублировать',
        menu_archive: 'Архивировать',
        menu_delete: 'Удалить проект',
        empty_title: 'В проекте пока нет строк',
        empty_sub: 'Добавьте строку вручную или импортируйте данные',
        msg_rename_saved: 'Название сохранено',
        msg_project_archived: 'Проект архивирован',
        msg_project_deleted: 'Проект удалён',
        msg_project_duplicated: 'Проект дублирован',
        msg_archive_err: 'Не удалось архивировать проект',
        msg_delete_proj_err: 'Не удалось удалить проект',
        msg_dup_err: 'Не удалось дублировать проект',
        archive_title: 'Архивировать проект?',
        archive_body:
          'Проект будет перемещён в архив. Вы сможете восстановить его позже.',
        btn_archive_confirm: 'Архивировать',
        delete_proj_title: 'Удалить проект?',
        delete_proj_body:
          'Проект «{{title}}» будет удалён. Это действие нельзя отменить.',
        btn_delete_confirm: 'Удалить',
      },
    },
    voices: {
      title: 'Голоса',
      sync_button: 'Синхронизировать с EL',
      sync_success: 'Синхронизировано',
      sync_error: 'Не удалось синхронизировать',
      export_button: 'Экспорт XLSX',
      filter: {
        all: 'Все',
        pending: 'В очереди',
        ready: 'Готовые',
        error: 'Ошибка',
        orphaned: 'Осиротевшие',
        deleted: 'Удалённые',
      },
      status: {
        pending: 'В процессе клонирования',
        ready: 'Готов',
        error: 'Ошибка',
        orphaned: 'Осиротевший',
        deleted: 'Удалён',
      },
      column: {
        name: 'Название',
        status: 'Статус',
        source: 'Источник',
        tasks: 'Задачи',
        created: 'Создан',
        actions: 'Действия',
      },
      empty: 'У вас ещё нет клонов голоса.',
      error_load: 'Не удалось загрузить голоса.',
      no_results: 'Нет результатов по выбранному фильтру.',
      task_count: '{{count}} задач',
      source_unavailable: 'Файл недоступен',
      stuck_badge: 'Завис',
      stuck_hint: 'Клонирование идёт слишком долго. Попробуйте удалить и создать заново.',
      action: {
        open_menu: 'Действия над голосом',
        rename: 'Переименовать',
        delete: 'Удалить',
        reclone: 'Переклонировать',
      },
      rename: {
        title: 'Переименовать голос',
        label: 'Название',
        submit: 'Сохранить',
        cancel: 'Отмена',
        success: 'Название обновлено',
        error: 'Не удалось переименовать',
        error_required: 'Введите название',
      },
      delete: {
        confirm: {
          title: 'Удалить голос «{{name}}»?',
          body: 'Голос будет удалён из EL и локально. Это действие нельзя отменить.',
          confirm: 'Удалить',
          cancel: 'Отмена',
        },
        success: 'Голос удалён',
        error: 'Не удалось удалить',
      },
      reclone: {
        success: 'Клонирование запущено',
        source_missing:
          'Файл-источник не найден. Создайте голос заново из нового аудио.',
        invalid_status:
          'Переклонировать можно только голоса со статусом «осиротевший» или «ошибка».',
        error: 'Не удалось переклонировать',
      },
      tabs: {
        clones: 'Клоны',
        history: 'EL история',
      },
      sample: {
        play: 'Проиграть образец',
      },
      upload: {
        title: 'Создать голос',
        drop_hint: 'Перетащите аудио-файл сюда или нажмите, чтобы выбрать',
        constraints: 'mp3, wav, m4a, до {{maxMb}} МБ',
        choose: 'Выбрать файл',
        progress: 'Загрузка… {{percent}}%',
        success: 'Файл загружен. Клонирование запущено.',
        invalid_type: 'Поддерживаются только аудио-файлы.',
        invalid_extension: 'Расширение «{{ext}}» не поддерживается.',
        too_large: 'Файл слишком большой (макс {{maxMb}} МБ).',
        upload_error: 'Не удалось загрузить файл.',
        clone_pending_hint:
          'Идёт клонирование… Список обновится автоматически.',
      },
    },
    el_history: {
      title: 'EL история',
      empty: 'История пуста.',
      error_load: 'Не удалось загрузить историю.',
      load_more: 'Загрузить ещё',
      column: {
        text: 'Текст',
        chars: 'Символы',
        created: 'Создан',
        actions: 'Действия',
      },
      play: 'Проиграть',
      chars_count: '{{count}} символов',
    },
    player: {
      now_playing: 'Сейчас играет',
      play: 'Проиграть',
      pause: 'Пауза',
      close: 'Закрыть',
      volume: 'Громкость',
      seek: 'Перемотать',
      load_error: 'Не удалось воспроизвести',
    },
    templates: {
      title: 'Шаблоны',
      subtitle:
        'Сохранённые аудио и видео для повторного использования в проектах и QT.',
      filter: {
        all: 'Все',
        audio: 'Аудио',
        video: 'Видео',
      },
      column: {
        name: 'Название',
        type: 'Тип',
        category: 'Категория',
        size: 'Размер',
        used: 'Использован',
        created: 'Создан',
        actions: 'Действия',
      },
      type: {
        audio: 'Аудио',
        video: 'Видео',
      },
      action: {
        pin: 'Закрепить',
        unpin: 'Открепить',
        delete: 'Удалить',
      },
      pinned: 'Закреплён',
      empty: 'Шаблонов ещё нет.',
      no_results: 'Нет результатов по выбранному типу.',
      error_load: 'Не удалось загрузить шаблоны.',
      use_count: '{{count}} раз',
      readonly_hint:
        'Просмотр шаблонов — подключение к проектам будет в следующем релизе.',
      upload_audio_button: 'Добавить аудио-шаблон',
      upload_video_button: 'Добавить видео-шаблон',
      upload_success: 'Шаблон загружен',
      upload_error: 'Не удалось загрузить шаблон',
      update_error: 'Не удалось обновить',
      delete_success: 'Шаблон удалён',
      delete_error: 'Не удалось удалить',
      delete_confirm: {
        title: 'Удалить шаблон «{{name}}»?',
        body: 'Файл будет удалён с диска, если другие шаблоны на него не ссылаются.',
        confirm: 'Удалить',
        cancel: 'Отмена',
      },
    },
    workspace: {
      personal: 'Личное пространство',
      switched: 'Переключено на {{name}}',
      switcher: {
        aria_label: 'Переключить workspace',
        manage: 'Управление workspaces',
        create: 'Создать workspace',
      },
      tabs: {
        list: 'Workspaces',
        members: 'Участники',
        settings: 'Настройки',
      },
      settings: {
        title: 'Настройки workspace',
        profile: 'Профиль',
        name: {
          label: 'Название',
          error_too_short: 'Название — от 2 до 100 символов',
          error_too_long: 'Название — от 2 до 100 символов',
        },
        emoji: {
          label: 'Emoji',
        },
        save: 'Сохранить',
        saved: 'Сохранено',
        save_error: 'Не удалось сохранить изменения',
        readonly_hint:
          'Только владелец или администратор может редактировать workspace.',
        unavailable_personal: 'Настройки недоступны в личном пространстве.',
      },
      danger: {
        title: 'Опасная зона',
      },
      leave: {
        button: 'Покинуть рабочее пространство',
        owner_hint: 'Передайте владение перед выходом.',
        pending_hint:
          'Это действие временно недоступно — endpoint pending Go-fix.',
        confirm: {
          title: 'Покинуть этот workspace?',
          body: 'Вы потеряете доступ к проектам, voices и файлам этого рабочего пространства. Это действие нельзя отменить.',
          confirm: 'Покинуть',
          cancel: 'Отмена',
        },
        success: 'Вы покинули рабочее пространство',
        error: 'Не удалось покинуть workspace',
      },
      delete: {
        button: 'Удалить рабочее пространство',
        dialog: {
          title: 'Удалить «{{name}}»?',
          body:
            'Это действие необратимо. Все проекты, voices, файлы этого рабочего пространства будут удалены.',
          confirm_label: 'Введите «{{name}}» для подтверждения',
          confirm_placeholder: 'Название workspace',
          confirm: 'Удалить навсегда',
          cancel: 'Отмена',
        },
        success: 'Workspace удалён',
        error: 'Не удалось удалить workspace',
      },
      members: {
        title: 'Участники',
        empty: 'Пока что только вы.',
        personal_unavailable: 'Участники недоступны в личном пространстве.',
        list_pending:
          'Список участников — endpoint pending Go-fix. Приглашение/смена роли/удаление работают.',
        error_load: 'Не удалось загрузить участников.',
        column: {
          name: 'Имя',
          email: 'Email',
          role: 'Роль',
          joined: 'Присоединился',
          actions: 'Действия',
        },
        role: {
          owner: 'Владелец',
          admin: 'Админ',
          member: 'Участник',
        },
        action: {
          open_menu: 'Действия над участником',
          change_role: 'Изменить роль',
          make_admin: 'Сделать админом',
          make_member: 'Сделать участником',
          remove: 'Удалить из workspace',
        },
        cannot_remove_owner: 'Владельца нельзя удалить',
        cannot_act_self: 'Действия над собой недоступны',
        change_role_success: 'Роль обновлена',
        change_role_error: 'Не удалось изменить роль',
        remove_success: 'Участник удалён',
        remove_error: 'Не удалось удалить участника',
        remove_confirm: {
          title: 'Удалить участника?',
          body: 'Пользователь потеряет доступ к этому рабочему пространству. Это действие нельзя отменить.',
          confirm: 'Удалить',
          cancel: 'Отмена',
        },
      },
      invite: {
        button: 'Пригласить',
        dialog: {
          title: 'Пригласить участника',
          role_label: 'Роль',
          role_hint:
            'Админ может приглашать, удалять участников и управлять workspace.',
          submit: 'Создать ссылку',
          cancel: 'Отмена',
          link_label: 'Ссылка-приглашение',
          link_hint:
            'Скопируйте и отправьте ссылку пользователю. Действует 7 дней.',
          copy: 'Скопировать',
          copied: 'Скопировано',
          close: 'Готово',
        },
        error: {
          generic: 'Не удалось создать приглашение.',
          already_member: 'Этот пользователь уже является участником.',
        },
      },
    },
    workspaces: {
      title: 'Рабочие пространства',
      create_button: 'Создать workspace',
      empty_title: 'У вас нет workspaces. Создайте первый!',
      empty_cta: 'Создать первый',
      active_badge: 'Активный',
      switch_action: 'Переключить',
      settings_action: 'Настройки',
      error_load: 'Не удалось загрузить workspaces. Попробуйте ещё раз',
      role: {
        owner: 'Владелец',
        admin: 'Админ',
        member: 'Участник',
      },
      create_dialog: {
        title: 'Создать workspace',
        name_label: 'Название',
        emoji_label: 'Emoji (необязательно)',
        submit: 'Создать',
        cancel: 'Отмена',
        success: 'Workspace создан',
        error_name_required: 'Введите название (1–50 символов)',
        error_name_taken: 'Workspace с таким названием уже существует',
      },
    },
    data: {
      title: 'Данные',
      subtitle: 'Все строки проектов. Следите за прогрессом, просматривайте и скачивайте результаты.',
      filter: {
        status_label: 'Статус',
        date_label: 'Дата',
        project_label: 'Проект',
        project_all: 'Все проекты',
        status: {
          all: 'Все',
          queued: 'В очереди',
          running: 'Выполняется',
          done: 'Готово',
          failed: 'Ошибка',
          cancelled: 'Отменено',
        },
        date: {
          all: 'Все',
          today: 'Сегодня',
          yesterday: 'Вчера',
          '3d': '3 дня',
          '7d': '7 дней',
          '30d': '30 дней',
        },
      },
      column: {
        name: 'Название',
        text: 'Текст',
        el_status: 'EL',
        fal_status: 'FAL',
        overall: 'Статус',
        updated: 'Обновлено',
        actions: 'Действия',
      },
      action: {
        play_audio: 'Проиграть аудио',
        play_video: 'Просмотр видео',
        download: 'Скачать',
      },
      empty: 'Нет строк по выбранным фильтрам.',
      error_load: 'Не удалось загрузить список.',
      download_error: 'Не удалось скачать',
      pagination: {
        summary: 'Страница {{page}} из {{total_pages}} • Всего: {{total}}',
        prev: 'Предыдущая',
        next: 'Следующая',
      },
      transfer_template: {
        title: 'Перенести как шаблон',
        source_hint: 'На основе #{{n}}{{name}}',
        target_label: 'Куда перенести',
        target_qt: 'Быстрые задачи',
        target_qt_hint: 'Новая строка в QT — без проекта',
        target_project: 'Проект',
        target_project_hint: 'Новая строка в выбранном проекте',
        no_projects: 'Нет доступных проектов',
        confirm: 'Перенести',
        success: 'Шаблон создан — переходим к редактированию',
        error: 'Не удалось создать шаблон',
        error_no_target: 'Выберите, куда перенести',
        note_prefix: 'Создано на основе',
      },
      clone_restart: {
        success: 'Создана новая задача с изменениями',
        error: 'Не удалось создать новую задачу',
      },
    },
    admin: {
      title: 'Админ-панель',
      subtitle: 'Пользователи, ожидающие регистрации и запросы на сброс пароля.',
      error_load: 'Не удалось загрузить',
      error_action: 'Не удалось выполнить действие',
      tab: {
        users: 'Пользователи',
        pending: 'Ожидают одобрения',
        resets: 'Сброс паролей',
      },
      users: {
        search_placeholder: 'Поиск по email или имени…',
        empty: 'Пользователи не найдены.',
        status: {
          all: 'Все',
          active: 'Активные',
          pending: 'Ожидают',
          blocked: 'Заблокированные',
        },
        chip: {
          active: 'активный',
          pending: 'ожидает',
          blocked: 'заблокирован',
        },
        column: {
          user: 'Пользователь',
          role: 'Роль',
          status: 'Статус',
          tasks: 'Задач',
          projects: 'Проектов',
          created: 'Создан',
          actions: 'Действия',
        },
        action: {
          approve: 'Одобрить',
          block: 'Заблокировать',
          unblock: 'Разблокировать',
          promote: 'Сделать админом',
          demote: 'Снять права админа',
          delete: 'Удалить',
        },
        approve_success: 'Пользователь одобрен',
        block_success: 'Заблокирован',
        unblock_success: 'Разблокирован',
        promote_success: 'Назначен админом',
        demote_success: 'Сняты права админа',
        demote_self_blocked: 'Нельзя снять права админа со своей учётки',
        delete_success: 'Удалён',
        delete_confirm: {
          title: 'Удалить «{{email}}»?',
          body: 'Пользователь и все его данные будут помечены удалёнными. Действие необратимо.',
          confirm: 'Удалить',
          cancel: 'Отмена',
        },
        pagination: {
          summary: 'Страница {{page}} из {{total_pages}} • Всего: {{total}}',
          prev: 'Предыдущая',
          next: 'Следующая',
        },
      },
      pending: {
        empty: 'Заявок на регистрацию нет.',
        registered_at: 'Зарегистрирован: {{date}}',
        approve_button: 'Одобрить',
      },
      resets: {
        empty: 'Запросов на сброс пароля нет.',
        requested_at: 'Запрошено: {{date}}',
        has_tg: 'Telegram подключён',
        no_tg: 'Нет Telegram — передайте пароль вручную',
        generate_button: 'Сгенерировать пароль',
        generated_success: 'Пароль сгенерирован и отправлен в Telegram',
        generated_no_tg: 'Пароль сгенерирован — Telegram не подключён, передайте вручную',
        generated_tg_failed:
          'Пароль сгенерирован, но Telegram не принял сообщение — передайте вручную',
        password_for: 'Пароль для {{user}}:',
        copy_hint: 'Передайте этот пароль пользователю:',
        copy: 'Скопировать',
        copied: 'Скопировано',
        copy_error: 'Не удалось скопировать',
      },
    },
    overlay: {
      title: 'Блики (Frame Overlay)',
      subtitle: 'Накладывайте картинки на отдельные кадры видео — добавляет «живой» эффект, чтобы ролик не выглядел 100% машинным.',
      videos_section: 'Видео (до {{max}} штук)',
      images_section: 'Картинки-блики',
      videos_empty: 'Перетащите или выберите до 10 видео',
      images_empty: 'Выберите 1-3 картинки PNG/JPEG',
      add_button: 'Добавить',
      remove: 'Убрать',
      frames_mode_label: 'Плотность',
      frames_mode_1: 'Разреженная',
      frames_mode_2: 'Плотная',
      frames_mode_hint: '1 = каждый N-й кадр, 2 = чаще',
      run_button: 'Обработать',
      upload_success: 'Задачи созданы',
      upload_error: 'Не удалось запустить',
      error_no_videos: 'Добавьте хотя бы одно видео',
      error_no_images: 'Добавьте хотя бы одну картинку',
      error_load: 'Не удалось загрузить список',
      empty: 'Задач пока нет.',
      tasks_title: 'Задачи обработки',
      expires_in: 'удалится через {{h}} ч',
      action: {
        play: 'Просмотр',
        download: 'Скачать',
        delete: 'Удалить',
      },
      delete_confirm: {
        title: 'Удалить эту задачу?',
        body: 'Файл будет удалён. Перед этим скачайте результат, если нужно.',
        confirm: 'Удалить',
        cancel: 'Отмена',
      },
      delete_success: 'Удалено',
      delete_error: 'Не удалось удалить',
      download_error: 'Не удалось скачать',
      play_error: 'Не удалось воспроизвести',
    },
    transcription: {
      title: 'Транскрипция',
      subtitle: 'Загрузи аудио или видео — получи текст с распознаванием спикеров',
      language_label: 'Язык',
      lang_auto: 'Автоопределение',
      upload_button: 'Загрузить файл',
      upload_success: 'Отправлено {{n}} файлов на транскрипцию',
      upload_error: 'Ошибка загрузки',
      sending: 'Загрузка...',
      empty: 'Транскрипций пока нет — загрузи файл выше',
      error_load: 'Не удалось загрузить список.',
      provider_label: 'Обработка:',
      provider: {
        el: 'ElevenLabs',
        el_hint: 'по подписке EL',
        whisper: 'Whisper',
        whisper_hint: '~$0.01 / мин',
        el_fal: 'EL Scribe',
        el_fal_hint: '$0.008 / мин',
      },
      drop_label: 'Перетащи файлы сюда или кликни',
      drop_hint: 'MP3, WAV, M4A, MP4, MOV — до 300 МБ · максимум 3 файла',
      select_files: 'Выбрать файлы',
      selected_files: 'Выбранные файлы',
      clear: 'Очистить',
      translate_after: 'Перевести после транскрипции',
      lang_ru: 'Русский',
      lang_uk: 'Українська',
      send: 'Отправить',
      feat_langs: '90+ языков',
      feat_speakers: 'Распознавание спикеров',
      feat_translate: 'Автоперевод',
      max_files_warn: 'Максимум {{n}} файлов за раз',
      file_too_large: 'Файл слишком большой (макс. {{n}} МБ)',
      no_files_warn: 'Выбери файлы для транскрипции',
      results: 'Результаты',
      refresh: 'Обновить',
      column: {
        date: 'Дата',
        file: 'Файл',
        duration: 'Длит.',
        language: 'Язык',
        original: 'Оригинал',
        translation: 'Перевод',
        status: 'Статус',
        actions: 'Действия',
        created: 'Создан',
      },
      status_label: {
        queued: 'В очереди',
        pending: 'В очереди',
        uploading: 'Загрузка',
        transcribing: 'Транскрипция',
        running: 'Транскрипция',
        translating: 'Перевод',
        done: 'Готово',
        failed: 'Ошибка',
        error: 'Ошибка',
        cancelled: 'Отменено',
      },
      action: {
        view: 'Открыть',
        delete: 'Удалить',
        retry: 'Перезапустить',
        download: 'Скачать',
      },
      delete_confirm: {
        title: 'Удалить эту транскрипцию?',
        body: 'Запись будет помечена как удалённая. Файл с диска уберёт фоновый cleanup.',
        confirm: 'Удалить',
        cancel: 'Отмена',
      },
      delete_success: 'Транскрипция удалена',
      delete_error: 'Ошибка удаления',
      retry_success: 'Запущено повторно',
      retry_error: 'Не удалось перезапустить',
      translate_started: 'Перевод запущен',
      translate_error: 'Ошибка перевода',
      error_human: {
        fal_balance: 'Закончился баланс FAL. Пополни счёт на fal.ai/dashboard/billing и попробуй снова.',
        fal_auth: 'Неверный API-ключ FAL. Проверь ключ в Настройках → API ключи.',
        el_auth: 'Неверный API-ключ ElevenLabs. Проверь ключ в Настройках → API ключи.',
        el_quota: 'Закончились кредиты ElevenLabs. Пополни подписку на elevenlabs.io.',
        rate_limit: 'Сервис получил слишком много запросов. Подожди 1–2 минуты и нажми ↻ снова.',
        server: 'Сервис временно недоступен. Попробуй через несколько минут.',
        timeout: 'Истекло время ожидания. Файл мог быть слишком длинным — попробуй ещё раз.',
        network: 'Ошибка сети. Проверь интернет и нажми ↻.',
        file_missing: 'Оригинальный файл уже удалён из хранилища. Загрузи файл заново.',
        no_key: 'API-ключ не настроен. Добавь его в Настройках → API ключи.',
        unknown: 'Неизвестная ошибка. Нажми ↻ или обратись в поддержку.',
        details_prefix: 'Детали',
      },
      popup_original: 'Оригинал',
      popup_translation: 'Перевод',
      no_text: 'Текст отсутствует',
      no_translation_yet: 'Перевода пока нет — выбери язык ниже и нажми «Перевод».',
      copy: 'Копировать',
      copied: 'Скопировано',
      copy_error: 'Не удалось скопировать',
      download_txt: 'Скачать .txt',
      chars: '{{n}} символов',
    },
    translate: {
      title: 'Перевод',
      subtitle: 'Автоматический перевод текста через DeepL API',
      no_key_title: 'Нет ключа DeepL',
      no_key_hint: 'Добавьте ключ в Настройки → API ключи (провайдер: deepl).',
      source_label: 'С языка',
      target_label: 'На язык',
      auto_detect: 'Определить язык',
      swap: 'Поменять местами',
      swap_title: 'Поменять языки местами',
      swap_auto_warn: 'Укажите язык источника для обмена',
      input_label: 'Оригинал',
      input_placeholder: 'Введите текст для перевода…',
      output_label: 'Перевод',
      output_placeholder: 'Здесь появится перевод',
      source_ph: 'Введите текст для перевода...',
      result_ph: 'Перевод появится тут...',
      detected: '(определён: {{lang}})',
      chars: 'символов',
      run_button: 'Перевести',
      translate_btn: 'Перевести',
      translate_fail: 'Ошибка перевода',
      translating: 'Переводим...',
      mode_auto: 'Авто',
      mode_btn: 'Кнопка',
      max_chars: 'Максимум 10 000 символов за один запрос',
      shortcut_hint: '— быстрый перевод',
      clear: 'Очистить',
      copy: 'Копировать перевод',
      copied: 'Скопировано в буфер обмена',
      copy_error: 'Не удалось скопировать',
      key_rotated: 'Переключено на следующий ключ DeepL',
      error_empty: 'Введите текст',
      error_call: 'Не удалось перевести',
      usage_label: 'Использовано символов',
      usage_title: 'Настройки DeepL ключа',
      search_lang: 'Найти язык...',
      recent: 'Последние',
      suggested: 'Предложенные',
      all_langs: 'Все языки',
      results_n: '{{n}} результатов',
      no_results: 'Ничего не найдено для «{{q}}»',
      nokey_title: 'DeepL API ключ не настроен',
      nokey_desc: 'Для использования перевода нужен DeepL API ключ.<br>Добавь его в настройках аккаунта.',
      go_settings: 'Перейти в настройки',
    },
    quick: {
      title: 'Быстрые задачи',
      subtitle: 'Создай видео комплексно — загрузи голоса и видео, добавь тексты и запусти',
      audio_section: 'Аудио (образец голоса)',
      video_section: 'Видео (опционально — для lip-sync)',
      upload_button: 'Загрузить',
      no_files: 'Файлов пока нет',
      texts_label: 'Тексты (один на строку)',
      texts_placeholder: 'Привет, Иван!\nПривет, Мария!\nПривет, Олег!',
      run_button: 'Запустить',
      file_delete: 'Удалить файл',
      delete_file_confirm: {
        title: 'Удалить «{{name}}»?',
        body: 'Файл будет удалён. Если он используется в активных задачах — задачи продолжат работать с копией.',
        confirm: 'Удалить',
        cancel: 'Отмена',
      },
      delete_row_confirm: {
        title: 'Удалить строку?',
        body: 'Текст: «{{text}}». Активные задачи этой строки будут отменены.',
        confirm: 'Удалить',
        cancel: 'Отмена',
      },
      upload_success_audio: 'Аудио загружено',
      upload_success_video: 'Видео загружено',
      upload_error: 'Не удалось загрузить файл',
      delete_error: 'Не удалось удалить',
      run_error_no_files: 'Выберите хотя бы один файл',
      run_error_no_text: 'Добавьте хотя бы один текст',
      run_success: 'Создано {{n}} строк',
      run_error: 'Не удалось запустить',
      download_error: 'Не удалось скачать',
      tasks_title: 'Последние задачи',
      tasks_empty: 'Запущенных задач пока нет.',
      task: {
        text: 'Текст',
        status: 'Статус',
        actions: 'Действия',
        play: 'Проиграть аудио',
        play_video: 'Просмотр видео',
        download: 'Скачать',
        delete: 'Удалить строку',
      },
      // ── Legacy `qt.*` keys ported verbatim from `lang-data.js` ────────
      add_package: '+ Добавить пакет',
      texts_to_all: 'Тексты → во все пакеты',
      texts_to_all_title: 'Скопировать тексты первого пакета во все остальные',
      settings: 'Настройки',
      run_all: '▶ Запустить всё',
      run_audio: 'Только голоса',
      stop_all: '⏹ СТОП ВСЁ',
      results: 'Результаты',
      empty: 'Задач пока нет — запусти первое задание выше',
      // ── React-only namespaced keys used by hooks / wrappers ──────────
      action: { clear_done: 'Очистить завершённые' },
      mode: {
        clone: 'Клон голоса',
        ready_voice: 'Готовый голос',
        templates: 'Из шаблонов',
        section_label: 'Режим быстрой задачи',
      },
      mode_hint: {
        clone: 'Загрузи аудио-референс для клонирования голоса + тексты для озвучки.',
        ready_voice:
          'Выбери ранее клонированный голос + видео — без расходов на повторное клонирование.',
        templates: 'Готовые шаблоны аудио/видео из твоей библиотеки.',
      },
    },
  },
}
